import { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { get, startCase, toLower } from 'lodash';
import { Skeleton } from 'antd';
import { getRouteDraftGenerateByBookId } from 'modules/v2/draft/routes/navigation';
import { fetchAllBooks } from 'modules/api';
import { Button, Title } from 'modules/v2/common/AtomicDesign/atoms';
import { FrontArrowIcon } from 'modules/v2/common/components/SvgIcons';
import { BookLabel } from 'modules/v2/pages/BookCatalog/components';
import { setBookLabel, setLabelColor } from 'modules/v2/pages/BookCatalog/utils';
import { getRouteBookCatalog } from 'modules/v2/routes/navigation';

import { getData } from 'modules/auth';
import { safeJSONParse } from 'modules/core/jsonUtils';
import { getImageCrossOrigin, retryImageWithCredentials } from 'modules/v2/common/utils';
import { formatBookList } from './utils';

const BooksContainer = (props) => {
  const [userData, setUserData] = useState(() => getData().userData);
  const [bookList, setBookList] = useState([]);
  const qc = useQueryClient();
  const history = useHistory();

  useEffect(() => {
    const original = Storage.prototype.setItem;
    Storage.prototype.setItem = function(key, value) {
      original.call(this, key, value);
      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          newValue: value,
          storageArea: this,
          url: window.location.href,
        }),
      );
    };
  }, []);

  const packages = userData?.afy_package;
  const shouldFetch = packages?.length > 0;

  const { isFetching, refetch } = useQuery(
    ['fetchAllBooks', packages],
    async () => {
      try {
        const response = await fetchAllBooks({
          limit: 3,
          isAllowed: true,
        });
        const books = (
          get(response, 'data.data.books') ||
          get(response, 'data.books') ||
          []
        ).filter(Boolean);
        setBookList(formatBookList(books));
        return books;
      } catch (error) {
        console.error('[fetchAllBooks] fetch error', error);
        throw error;
      }
    },
    {
      enabled: shouldFetch,
      refetchOnMount: true,
      onSuccess: () => {
        console.log('onSuccess refetching books');
      },
      onError: (err) => {
        console.error('[fetchAllBooks] error', err);
      },
    },
  );

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'userData') {
        const updated = e.newValue ? safeJSONParse(e.newValue) : null;
        if (JSON.stringify(updated?.afy_packages) !== JSON.stringify(userData?.afy_packages)) {
          setUserData(updated);
          refetch();
        } else {
          setUserData(updated);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [qc, userData?.afy_packages, refetch]);

  const handleSeeAllBooks = () => {
    history.push(getRouteBookCatalog());
  };

  return (
    <div className="flex flex-col">
      <Title className="mb-3.5">My books</Title>
      <div className="flex flex-col">
        <div className="flex flex-column justify-start h-[280px] gap-2 clear-right w-full flex-wrap max-sm:justify-center max-lg:h-fit overflow-hidden">
          {isFetching ? (
            <>
              <div className="h-[280px] flex-1">
                <Skeleton loading active paragraph={{ rows: 6 }} />
              </div>
              <div className="h-[280px] flex-1">
                <Skeleton loading active paragraph={{ rows: 6 }} />
              </div>
              <div className="h-[280px] flex-1">
                <Skeleton loading active paragraph={{ rows: 6 }} />
              </div>
            </>
          ) : null}

          {!isFetching &&
            bookList.map((book) => {
              const hasDraft = book?.title;
              return (
                <div key={book.id} className="w-[148px]">
                  <Link to={getRouteDraftGenerateByBookId(book.id)} className="relative block">
                    <div className="rounded-md overflow-hidden">
                      <img
                        src={`${book.imageUrl}?random=33`}
                        height="280px" // setting height directly to avoid CLS
                        className="w-[148px] h-[224px] rounded-md object-cover hover:transform hover:scale-110 transition-transform duration-300 ease-in-out"
                        alt={book.title}
                        crossOrigin={getImageCrossOrigin(book.imageUrl)}
                        onError={retryImageWithCredentials}
                      />
                    </div>

                    {hasDraft && (
                      <BookLabel
                        label={setBookLabel(book?.status, book?.isAllowed)}
                        className={setLabelColor(book?.status)}
                      />
                    )}

                    <h3 className="text-sm font-medium mt-2 mb-0 pb-0 truncate tracking-normal">
                      {startCase(toLower(book.title))}
                    </h3>
                    <div className="text-sm text-neutral-500 font-medium truncate">{book.name}</div>
                  </Link>
                </div>
              );
            })}
        </div>
        <div className="relative mt-3">
          <Button size="xs" color="outlined" onClick={handleSeeAllBooks}>
            <div className="flex items-center justify-center">
              See all book catalog <FrontArrowIcon className="ml-2" />
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BooksContainer;
